import React, {FC, useEffect, useState} from 'react'
import {StatisticsWidget6, TablesWidget13, ChartSozlesme} from '../../../_metronic/partials/widgets'
import BranchStatisticsTable from './BranchStatisticsTable'
import GeneralStatusPanel from './GeneralStatusPanel'
import {
  getAvailableCarCount,
  getLateDepartureCars,
  getLateReturnCars,
  getRentedCarCount,
  getReservedCarCount,
  getServiceCarCount,
  getTodayDepartureCars,
  getTodayDeparturedCars,
  getTodayReturnCars,
  getTodayReturnedCars,
  getTomorrowDepartureCars,
  getTomorrowReturnCars,
} from './Api'
import {dashboardTabs, IDepartureCarModel, IReturnedCarModel} from './DashboardModel'
import {StatisticsCount} from '../../../_metronic/partials/widgets/statistics/StatisticsCount'
import SubTabHeader from '../../../shared/components/SubTabHeader'
import VehicleTrackingSystem, {
  TabHeader,
  TabKey,
} from '../../../shared/components/VehicleTrackingSystem'
import {useTabCars} from '../../../shared/hooks/useTabCars'
import {Pagination} from '../../../shared/models/ApiModels'

const DashboardPage: FC = () => {
  const [rentedCarCount, setRentedCarCount] = useState<string>('0')
  const [availableCarCount, setAvailableCarCount] = useState<string>('0')
  const [reservedCarCount, setReservedCarCount] = useState<string>('0')
  const [serviceCarCount, setServiceCarCount] = useState<string>('0')
  const [tabSub, setTabSub] = useState<string | 'short'>('short')
  const [tabReturn, setTabReturn] = useState<TabKey>('today')
  const [tabDeparture, setTabDeparture] = useState<TabKey>('today')
  const [returnPagination, setReturnPagination] = useState<Pagination>({
    page: 0,
    size: 10,
  })
  const [departurePagination, setDeparturePagination] = useState<Pagination>({
    page: 0,
    size: 10,
  })

  useEffect(() => {
    Promise.all([
      getRentedCarCount(),
      getAvailableCarCount(),
      getReservedCarCount(),
      getServiceCarCount(),
    ]).then(([rtc, avc, rsc, src]) => { 
      setRentedCarCount((rtc.data?.payload ?? 0).toString())
      setAvailableCarCount((avc.data?.payload ?? 0).toString())
      setReservedCarCount((rsc.data?.payload ?? 0).toString())
      setServiceCarCount((src.data?.payload ?? 0).toString()) 
    })
  }, [])

  const {
    cars: departureCars,
    counts: departureCounts,
    loading: loadingDeparture,
  } = useTabCars<IDepartureCarModel>(
    tabDeparture,
    {
      late: getLateDepartureCars,
      today: getTodayDepartureCars,
      past: getTodayDeparturedCars,
      tomorrow: getTomorrowDepartureCars,
    },
    departurePagination.page,
    departurePagination.size
  )

  const {
    cars: returnCars,
    counts: returnCounts,
    loading: loadingReturn,
  } = useTabCars<IReturnedCarModel>(
    tabReturn,
    {
      late: getLateReturnCars,
      today: getTodayReturnCars,
      past: getTodayReturnedCars,
      tomorrow: getTomorrowReturnCars,
    },
    returnPagination.page,
    returnPagination.size
  )

  useEffect(() => {
    setReturnPagination((p) => ({...p, page: 0}))
  }, [tabReturn])

  useEffect(() => {
    setDeparturePagination((p) => ({...p, page: 0}))
  }, [tabDeparture])

  const returnTabs: TabHeader<TabKey>[] = [
    {id: 'late' as const, label: 'Dönüşü Gecikenler', count: returnCounts.late},
    {id: 'today' as const, label: 'Bugün Dönecekler', count: returnCounts.today},
    {id: 'past' as const, label: 'Bugün Dönenler', count: returnCounts.past},
    {id: 'tomorrow' as const, label: 'Yarın Dönecekler', count: returnCounts.tomorrow},
  ]

  const departureTabs: TabHeader<TabKey>[] = [
    {id: 'late', label: 'Çıkışı Gecikenler', count: departureCounts.late},
    {id: 'today', label: 'Bugün Çıkacaklar', count: departureCounts.today},
    {id: 'past', label: 'Bugün Çıkanlar', count: departureCounts.past},
    {id: 'tomorrow', label: 'Yarın Çıkacaklar', count: departureCounts.tomorrow},
  ]

  return (
    <>
      <SubTabHeader tab={tabSub} setTab={setTabSub} tabs={dashboardTabs} />
      {tabSub === 'short' && (
        <>
          <div className='row g-5 g-xl-8'>
            <div className='col-xl-3'>
              <StatisticsCount
                className='card-xl-stretch mb-xl-8'
                color='info'
                iconColor='white'
                title={rentedCarCount}
                description='Kiradaki Araçlar'
                percentage='Toplamdan %91.29'
                icon='bi-hand-thumbs-up'
              />
            </div>
            <div className='col-xl-3'>
              <StatisticsCount
                className='card-xl-stretch mb-xl-8'
                color='success'
                iconColor='white'
                title={availableCarCount}
                description='Boştaki Araçlar'
                percentage='Toplamdan %6.84'
                icon='bi-car-front'
              />
            </div>
            <div className='col-xl-3'>
              <StatisticsCount
                className='card-xl-stretch mb-5 mb-xl-8'
                color='danger'
                iconColor='white'
                title={serviceCarCount}
                description='Servisteki Araçlar'
                percentage='Toplamdan %1.58'
                icon='bi-tools'
              />
            </div>
            <div className='col-xl-3'>
              <StatisticsCount
                className='card-xl-stretch mb-5 mb-xl-8'
                color='warning'
                iconColor='white'
                title={reservedCarCount}
                description='Açık Rezervasyonlar'
                percentage='Toplamdan %36.84'
                icon='bi-clock'
              />
            </div>
          </div>
          <div className='row g-5 g-xl-8'>
            <div className='col-xl-6'>
              <VehicleTrackingSystem
                name='in'
                header='Dönüşler'
                tabs={returnTabs}
                activeTab={tabReturn}
                setTab={setTabReturn}
                data={returnCars}
                loading={loadingReturn}
              />
            </div>
            <div className='col-xl-6'>
              <VehicleTrackingSystem
                name='out'
                header='Çıkışlar'
                tabs={departureTabs}
                activeTab={tabDeparture}
                setTab={setTabDeparture}
                data={departureCars}
                loading={loadingDeparture}
              />
            </div>
          </div>
        </>
      )}
      {tabSub === 'chart' && (
        <>
          <div className='row g-5 g-xl-8'>
            <ChartSozlesme className='card-xl-stretch mb-xl-8' />
          </div>
          <div className='row g-5 g-xl-8'>
            <div className='col-xl-4'>
              <StatisticsWidget6
                className='card-xl-stretch mb-xl-8'
                color='success'
                title=''
                description='Dolu Araç Oranı'
                progress='95%'
              />
            </div>

            <div className='col-xl-4'>
              <StatisticsWidget6
                className='card-xl-stretch mb-xl-8'
                color='warning'
                title=''
                description='Servisteki Araç Oranı'
                progress='1%'
              />
            </div>

            <div className='col-xl-4'>
              <StatisticsWidget6
                className='card-xl-stretch mb-xl-8'
                color='primary'
                title=''
                description='Müşteri Memnuniyet Oranı'
                progress='99%'
              />
            </div>
          </div>
        </>
      )}
      {tabSub === 'park' && (
        <>
          <BranchStatisticsTable /> 
        </>
      )}
      {tabSub === 'detail' && (
        <>
          <GeneralStatusPanel />
        </>
      )}
    </>
  )
}

export {DashboardPage}
